// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — where several pins' series sit when they share one pair of axes.
 * Ported from AYNI 2D (`src/shared/measurement-chart-geometry.ts`) for the
 * Data tab's graph, and meant to be shared with the VR Data panel (Phase 4).
 *
 * Stacking a chart per pin makes them impossible to compare: each gets its
 * own vertical scale, so two traces of the same shape can mean wildly
 * different things. One shared y-axis across every trace is what makes
 * "this one is higher" a statement about the data rather than the layout.
 *
 * Geometry only, in 0..1 space, so the browser can draw it as SVG and VR as
 * canvas without either owning the arithmetic.
 */

import { thinSeries, type MeasurementSeries } from './series'

export interface ChartPoint {
  x: number
  y: number
}

export type RailState = 'data' | 'nodata' | 'unsampled'

export interface ChartTrace {
  id: string
  color: string
  /** Runs of consecutive readings; a break is a gap the series cannot span. */
  segments: ChartPoint[][]
  /** Readings standing alone between gaps, which no run would draw. */
  dots: ChartPoint[]
  /** Per-slot state, for a coverage rail beneath the chart. */
  rail: RailState[]
  /** AYNI: the series as drawn (thinned to the chart's columns), so a hover reads what the line shows. */
  series: MeasurementSeries
}

export interface ChartGeometry {
  traces: ChartTrace[]
  /** The shared vertical range every trace is drawn against. */
  min: number
  max: number
  /** Slots drawn (columns, when thinned), so a caller can size a rail. */
  slots: number
  /**
   * AYNI: where each of `ChartOptions.references` sits (0 top … 1 bottom,
   * as the traces' y), in the same order; null for one too far from the
   * data to be drawn.
   */
  references: Array<number | null>
}

export interface ChartTraceInput {
  id: string
  color: string
  series: MeasurementSeries
}

export interface ChartOptions {
  /**
   * AYNI: join a run across up to this many slots nobody has read yet.
   * Sampling a playing video every 200 ms misses the odd frame, and
   * without this a trace that is really continuous draws as dust. Slots
   * read as no data always break the run, and the rail still shows every
   * unread slot, so nothing claims coverage it does not have. 0 (AYNI 2D's
   * behaviour) breaks at every hole.
   */
  bridge?: number
  /**
   * AYNI: at most this many points across, about one per pixel. A series
   * keeps a slot per frame (thousands on a long stream); past this each
   * column draws the mean of the frames read in it (`thinSeries`), so
   * every lap adds to the line instead of redrawing it.
   */
  columns?: number
  /**
   * AYNI: reference values (a boundary such as 1.5 °C, `referenceLines.ts`).
   * One within the data's range is placed on it; one beyond, but no
   * further than `REFERENCE_REACH` of the data's span, stretches the axis
   * to take it in, so a pin still below the line shows how far it has to
   * go. One further off is left out (its y is null), so a line far away
   * never flattens the data into a strip.
   */
  references?: readonly number[]
}

/** AYNI: how far past the data, as a share of its span, a reference line still stretches the axis. */
export const REFERENCE_REACH = 0.5

/**
 * Null when nothing has any data yet: an empty chart reads as a broken
 * instrument rather than one still filling in.
 */
export function buildSeriesChart(inputs: ReadonlyArray<ChartTraceInput>, options: ChartOptions = {}): ChartGeometry | null {
  const bridge = Math.max(0, Math.floor(options.bridge ?? 0))
  const columns = options.columns
  if (columns !== undefined) inputs = inputs.map((input) => ({ ...input, series: thinSeries(input.series, columns) }))
  let min = Infinity
  let max = -Infinity
  let slots = 0
  let any = false
  for (const input of inputs) {
    slots = Math.max(slots, input.series.slots.length)
    for (const slot of input.series.slots) {
      if (!slot.sampled || slot.value == null) continue
      any = true
      if (slot.value < min) min = slot.value
      if (slot.value > max) max = slot.value
    }
  }
  if (!any || slots < 2) return null

  // AYNI: reference lines close enough to the data join its range.
  const refs = options.references ?? []
  const dataSpan = max - min
  const reach = REFERENCE_REACH * (dataSpan > 0 ? dataSpan : Math.abs(max) || 1)
  const taken = refs.map((v) => Number.isFinite(v) && v >= min - reach && v <= max + reach)
  refs.forEach((v, i) => {
    if (!taken[i]) return
    if (v < min) min = v
    if (v > max) max = v
  })

  const span = max - min
  const traces: ChartTrace[] = []
  for (const input of inputs) {
    const slotList = input.series.slots
    const last = Math.max(1, slotList.length - 1)
    const segments: ChartPoint[][] = []
    const dots: ChartPoint[] = []
    const rail: RailState[] = []
    let run: ChartPoint[] = []
    let unread = 0
    const flush = () => {
      if (run.length > 1) segments.push(run)
      // A reading standing alone is still a reading someone took: a line
      // needs two points, a measurement does not.
      else if (run.length === 1) dots.push(run[0])
      run = []
    }
    for (let i = 0; i < slotList.length; i++) {
      const slot = slotList[i]
      rail.push(!slot.sampled ? 'unsampled' : slot.value == null ? 'nodata' : 'data')
      if (!slot.sampled && run.length && ++unread <= bridge) continue
      if (!slot.sampled || slot.value == null) { flush(); unread = 0; continue }
      unread = 0
      // A flat set has no span to stretch against; hold it mid-height.
      run.push({ x: i / last, y: span > 0 ? 1 - (slot.value - min) / span : 0.5 })
    }
    flush()
    traces.push({ id: input.id, color: input.color, segments, dots, rail, series: input.series })
  }
  const references = refs.map((v, i) => (!taken[i] ? null : span > 0 ? 1 - (v - min) / span : 0.5))
  return { traces, min, max, slots, references }
}
